<!--
  HyperCX unified Kubernetes access view.

  This is the only Kubernetes detail sub-tab exposed by HyperCX. It merges the
  useful access material from the upstream Kubernetes tab with HyperCX node
  access information so the UI does not present two separate Access sections.
-->
<template>
  <div class="hypercx-kubernetes-access">
    <a-alert
      v-if="!isCloudManaged"
      type="info"
      show-icon
      :message="$t('message.hypercx.kubernetes.external.access')"
      style="margin-bottom: 16px" />

    <template v-else>
      <a-card
        size="small"
        :title="$t('label.hypercx.kubernetes.cluster.access')"
        :loading="loading"
        style="margin-bottom: 16px">
        <a-descriptions
          class="hypercx-kubernetes-cluster-summary"
          :column="1"
          bordered
          size="small">
          <a-descriptions-item :label="$t('label.hypercx.kubernetes.node.user')">
            <a-typography-text copyable>{{ nodeUser || 'cloud' }}</a-typography-text>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('label.hypercx.kubernetes.ssh.key.pair')">
            {{ resource.keypair || $t('label.hypercx.not.configured') }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('label.hypercx.kubernetes.public.ip')">
            <a-typography-text v-if="publicIp" copyable>{{ publicIp }}</a-typography-text>
            <span v-else>{{ $t('label.hypercx.not.available') }}</span>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('label.hypercx.kubernetes.api')">
            <a-typography-text v-if="resource.endpoint" copyable>{{ resource.endpoint }}</a-typography-text>
            <span v-else>{{ $t('label.hypercx.not.available') }}</span>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('label.hypercx.kubernetes.kubectl.version')">
            {{ kubernetesVersion || $t('label.hypercx.not.available') }}
          </a-descriptions-item>
        </a-descriptions>
      </a-card>

      <a-card
        size="small"
        :title="$t('label.hypercx.kubernetes.kubeconfig')"
        style="margin-bottom: 16px">
        <a-alert
          type="info"
          show-icon
          style="margin-bottom: 12px"
          :message="$t('message.hypercx.kubernetes.kubeconfig.help')" />
        <a-space wrap>
          <a-button type="primary" :loading="downloadingKubeconfig" @click="downloadKubeconfig">
            {{ $t('label.hypercx.kubernetes.download.kubeconfig') }}
          </a-button>
        </a-space>
      </a-card>

      <a-card
        size="small"
        :title="$t('label.hypercx.kubernetes.kubectl')"
        style="margin-bottom: 16px">
        <a-alert
          type="info"
          show-icon
          style="margin-bottom: 12px"
          :message="$t('message.hypercx.kubernetes.kubectl.help', { version: kubernetesVersion || '' })" />
        <a-space direction="vertical" style="width: 100%">
          <a-button block :disabled="!kubectlDownloads.linux" @click="openUrl(kubectlDownloads.linux)">
            Linux amd64
          </a-button>
          <a-button block :disabled="!kubectlDownloads.windows" @click="openUrl(kubectlDownloads.windows)">
            Windows amd64
          </a-button>
          <a-button block :disabled="!kubectlDownloads.macos" @click="openUrl(kubectlDownloads.macos)">
            macOS amd64
          </a-button>
          <a-button block :disabled="!kubectlDownloads.macosArm" @click="openUrl(kubectlDownloads.macosArm)">
            macOS arm64
          </a-button>
        </a-space>
      </a-card>

      <a-alert
        type="info"
        show-icon
        style="margin-bottom: 16px"
        :message="$t('message.hypercx.kubernetes.private.key', { product: 'HyperCX' })" />

      <a-card
        size="small"
        :title="$t('label.hypercx.kubernetes.ssh.access')"
        :loading="loading"
        style="margin-bottom: 16px">
        <a-alert
          type="info"
          show-icon
          style="margin-bottom: 12px"
          :message="$t('message.hypercx.kubernetes.ssh.public.ip')" />
        <a-empty v-if="nodes.length === 0" :description="$t('message.hypercx.kubernetes.no.nodes')" />
        <a-table
          v-else
          :columns="columns"
          :data-source="nodes"
          :pagination="false"
          row-key="id"
          size="small">
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'role'">
              {{ $t(`label.hypercx.kubernetes.role.${record.role}`) }}
            </template>
            <template v-else-if="column.key === 'command'">
              <div v-if="record.sshPort" class="hypercx-kubernetes-command-row">
                <code>{{ sshCommand(record) }}</code>
                <a-button size="small" @click="copyText(sshCommand(record))">
                  {{ $t('label.hypercx.copy') }}
                </a-button>
              </div>
              <span v-else>{{ $t('message.hypercx.kubernetes.ssh.forwarding.unavailable') }}</span>
            </template>
            <template v-else-if="column.key === 'sshPort'">
              {{ record.sshPort || '—' }}
            </template>
          </template>
        </a-table>
      </a-card>

      <a-card
        v-if="resource.consoleendpoint"
        size="small"
        :title="$t('label.hypercx.kubernetes.web.access')"
        style="margin-bottom: 16px">
        <a-alert
          type="info"
          show-icon
          style="margin-bottom: 12px"
          :message="$t('message.hypercx.kubernetes.web.access')" />
        <a-button type="primary" @click="openUrl(resource.consoleendpoint)">
          {{ $t('label.hypercx.kubernetes.open.web.console') }}
        </a-button>
      </a-card>

      <a-card size="small" :title="$t('label.hypercx.kubernetes.dashboard.access')">
        <a-alert
          type="info"
          show-icon
          style="margin-bottom: 12px"
          :message="$t('message.hypercx.kubernetes.kubeconf')" />

        <div class="hypercx-kubernetes-access-step">
          <strong>1. {{ $t('label.hypercx.kubernetes.dashboard.token') }}</strong>
          <div class="hypercx-kubernetes-command-row">
            <code>{{ dashboardTokenCommand }}</code>
            <a-button size="small" @click="copyText(dashboardTokenCommand)">
              {{ $t('label.hypercx.copy') }}
            </a-button>
          </div>
        </div>

        <div class="hypercx-kubernetes-access-step">
          <strong>2. {{ $t('label.hypercx.kubernetes.dashboard.proxy') }}</strong>
          <div class="hypercx-kubernetes-command-row">
            <code>{{ dashboardProxyCommand }}</code>
            <a-button size="small" @click="copyText(dashboardProxyCommand)">
              {{ $t('label.hypercx.copy') }}
            </a-button>
          </div>
        </div>

        <div class="hypercx-kubernetes-access-step">
          <strong>3. {{ $t('label.hypercx.kubernetes.dashboard.open') }}</strong>
          <div class="hypercx-kubernetes-command-row">
            <code>{{ dashboardUrl }}</code>
            <a-button size="small" @click="copyText(dashboardUrl)">
              {{ $t('label.hypercx.copy') }}
            </a-button>
            <a-button size="small" type="primary" @click="openUrl(dashboardUrl)">
              {{ $t('label.hypercx.kubernetes.open.dashboard') }}
            </a-button>
          </div>
        </div>

        <a-alert
          type="info"
          show-icon
          :message="$t('message.hypercx.kubernetes.dashboard.login.token')" />
      </a-card>
    </template>
  </div>
</template>

<script>
import { getAPI } from '@/api'

export default {
  name: 'KubernetesAccessTab',
  props: {
    resource: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: false,
      downloadingKubeconfig: false,
      portForwardingRules: [],
      dashboardTokenCommand: 'kubectl --kubeconfig kube.conf -n kubernetes-dashboard create token kubernetes-dashboard',
      dashboardProxyCommand: 'kubectl --kubeconfig kube.conf proxy',
      dashboardUrl: 'http://localhost:8001/api/v1/namespaces/kubernetes-dashboard/services/https:kubernetes-dashboard:/proxy/'
    }
  },
  computed: {
    columns () {
      return [
        { title: this.$t('label.hypercx.kubernetes.role'), dataIndex: 'role', key: 'role' },
        { title: this.$t('label.hypercx.kubernetes.node'), dataIndex: 'name', key: 'name' },
        { title: this.$t('label.hypercx.kubernetes.private.ip'), dataIndex: 'privateIp', key: 'privateIp' },
        { title: this.$t('label.hypercx.kubernetes.ssh.port'), dataIndex: 'sshPort', key: 'sshPort' },
        { title: this.$t('label.hypercx.kubernetes.ssh.command'), key: 'command' }
      ]
    },
    isCloudManaged () {
      return this.resource?.clustertype === 'CloudManaged'
    },
    kubernetesVersion () {
      return String(this.resource?.kubernetesversionname || '').trim()
    },
    kubectlDownloads () {
      const version = this.kubernetesVersion
      if (!/^v\d+\.\d+\.\d+/.test(version)) {
        return { linux: '', windows: '', macos: '', macosArm: '' }
      }
      const base = `https://dl.k8s.io/release/${version}/bin`
      return {
        linux: `${base}/linux/amd64/kubectl`,
        windows: `${base}/windows/amd64/kubectl.exe`,
        macos: `${base}/darwin/amd64/kubectl`,
        macosArm: `${base}/darwin/arm64/kubectl`
      }
    },
    publicIp () {
      if (this.resource?.ipaddress) {
        return this.resource.ipaddress
      }
      const endpoint = String(this.resource?.endpoint || '')
      const match = endpoint.match(/^https?:\/\/([^/:]+)/i)
      return match ? match[1] : ''
    },
    nodeUser () {
      const vms = this.resource?.virtualmachines || []
      for (const vm of vms) {
        const value = vm?.details?.controlNodeLoginUser
        if (value) {
          return value
        }
      }
      return this.isCloudManaged ? 'cloud' : ''
    },
    nodes () {
      const vms = this.resource?.virtualmachines || []
      return vms
        .filter(vm => String(vm?.isexternalnode || 'false') !== 'true')
        .map(vm => {
          const privateIp = (vm.nic || []).find(nic => nic.isdefault === true || String(nic.isdefault) === 'true')?.ipaddress ||
            vm.nic?.[0]?.ipaddress || ''
          const rule = this.portForwardingRules.find(item =>
            String(item.virtualmachineid || '') === String(vm.id || '') &&
            Number(item.privateport) === 22 &&
            String(item.protocol || '').toLowerCase() === 'tcp') ||
            this.portForwardingRules.find(item =>
              String(item.vmguestip || item.privateip || '') === String(privateIp) &&
              Number(item.privateport) === 22 &&
              String(item.protocol || '').toLowerCase() === 'tcp')
          return {
            id: vm.id,
            name: vm.name,
            role: this.nodeRole(vm),
            privateIp,
            sshPort: rule?.publicport ? Number(rule.publicport) : null
          }
        })
        .sort((a, b) => {
          const order = { control: 0, etcd: 1, worker: 2 }
          const roleDelta = (order[a.role] ?? 9) - (order[b.role] ?? 9)
          return roleDelta || String(a.name).localeCompare(String(b.name))
        })
    }
  },
  watch: {
    resource: {
      handler () {
        this.loadAccessRules()
      },
      deep: true
    }
  },
  created () {
    this.loadAccessRules()
  },
  methods: {
    nodeRole (vm) {
      if (String(vm?.isetcdnode || 'false') === 'true') {
        return 'etcd'
      }
      if (String(vm?.name || '').includes('-control-')) {
        return 'control'
      }
      return 'worker'
    },
    loadAccessRules () {
      this.portForwardingRules = []
      if (!this.isCloudManaged || !this.resource?.ipaddressid) {
        return
      }
      this.loading = true
      getAPI('listPortForwardingRules', {
        ipaddressid: this.resource.ipaddressid,
        listall: true
      }).then(json => {
        this.portForwardingRules = json?.listportforwardingrulesresponse?.portforwardingrule || []
      }).catch(() => {
        this.portForwardingRules = []
      }).finally(() => {
        this.loading = false
      })
    },
    extractKubeconfig (value) {
      if (typeof value === 'string') {
        if (value.includes('apiVersion:') && value.includes('clusters:')) {
          return value
        }
        return ''
      }
      if (!value || typeof value !== 'object') {
        return ''
      }
      for (const key of ['kubeconfig', 'config', 'data']) {
        if (typeof value[key] === 'string' && value[key].includes('apiVersion:')) {
          return value[key]
        }
      }
      for (const child of Object.values(value)) {
        const candidate = this.extractKubeconfig(child)
        if (candidate) {
          return candidate
        }
      }
      return ''
    },
    async downloadKubeconfig () {
      if (!this.resource?.id) {
        return
      }
      this.downloadingKubeconfig = true
      try {
        const response = await getAPI('getKubernetesClusterConfig', { id: this.resource.id })
        const kubeconfig = this.extractKubeconfig(response)
        if (!kubeconfig) {
          throw new Error('Kubeconfig was not returned by CloudStack')
        }
        const blob = new Blob([kubeconfig], { type: 'application/x-yaml;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = 'kube.conf'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        this.$message.error(this.$t('message.hypercx.kubernetes.kubeconfig.error'))
      } finally {
        this.downloadingKubeconfig = false
      }
    },
    sshCommand (node) {
      if (!this.publicIp || !node?.sshPort) {
        return ''
      }
      return `ssh -i /path/to/private-key -p ${node.sshPort} ${this.nodeUser || 'cloud'}@${this.publicIp}`
    },
    copyText (text) {
      if (text && navigator?.clipboard) {
        navigator.clipboard.writeText(text)
      }
    },
    openUrl (url) {
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    }
  }
}
</script>

<style scoped>
.hypercx-kubernetes-access {
  width: 100%;
}

.hypercx-kubernetes-command-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.hypercx-kubernetes-access-step {
  margin-bottom: 16px;
}

/*
 * Ant Design's bordered Descriptions component uses a fixed light label
 * background upstream.  Use neutral translucent surfaces so the approved
 * Cluster access layout remains readable in both HyperCX light and dark modes.
 */
.hypercx-kubernetes-cluster-summary :deep(.ant-descriptions-view) {
  border-color: rgba(127, 127, 127, 0.28);
}

.hypercx-kubernetes-cluster-summary :deep(.ant-descriptions-item-label) {
  background: rgba(127, 127, 127, 0.10);
  color: inherit;
  border-color: rgba(127, 127, 127, 0.28);
}

.hypercx-kubernetes-cluster-summary :deep(.ant-descriptions-item-content) {
  background: transparent;
  color: inherit;
  border-color: rgba(127, 127, 127, 0.28);
}

.hypercx-kubernetes-command-row code {
  display: inline-block;
  max-width: 100%;
  padding: 4px 8px;
  border: 1px solid rgba(127, 127, 127, 0.28);
  border-radius: 4px;
  background: rgba(127, 127, 127, 0.10);
  color: inherit;
  overflow-wrap: anywhere;
  white-space: normal;
}
</style>
